import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { SfDataService } from '../../services/sf-data.service';
import { HubReportStatusService } from '../../services/hub-report-status.service';

interface UploadResult {
  success?: boolean;
  status?: string;
  message?: string;
  errors?: Array<{ row?: number | null; column?: string; message?: string }>;
  rowsProcessed?: number;
  rowsUpdated?: number;
  rowsFailed?: number;
  weekEnding?: string;
}

interface WeekOption { value: string; label: string; }

@Component({
  selector: 'app-hub-report-upload',
  templateUrl: './hub-report-upload.component.html',
  styleUrls: ['./hub-report-upload.component.scss'],
  standalone: false
})
export class HubReportUploadComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  fileName = '';
  base64Content = '';
  uploading = false;
  result: UploadResult | null = null;
  fileError = '';

  weekOptions: WeekOption[] = [];
  selectedWeek = '';
  currentWeekEnding = '';
  reportDue = false;
  // True once the report is due and we're on day 4 (Thursday) or later — escalates to a red/flashing banner.
  reportUrgent = false;

  // Columns the hub fills in (everything else is prefilled by Salesforce).
  readonly hubColumns = [
    'Bank Balance', 'Other Weekly Expense', 'Payroll Expense', 'Employer Tax',
    'Commissions (Cash)', 'Bills Outstanding', 'Pending CC', 'Comments'
  ];

  constructor(
    private sfDataService: SfDataService,
    private statusService: HubReportStatusService
  ) { }

  ngOnInit(): void {
    this.statusService.context.pipe(takeUntil(this.destroy$)).subscribe(ctx => {
      if (!ctx) { return; }
      this.currentWeekEnding = ctx.currentWeekEnding || '';
      this.reportDue = !!ctx.missingTemplate;
      // Only the most recent week ending is selectable — never let the hub pick an older week.
      const latest = ctx.currentWeekEnding || (ctx.weekOptions || [])[0];
      this.weekOptions = latest ? [{ value: latest, label: this.formatWeek(latest) }] : [];
      this.selectedWeek = latest || '';
    });
    this.statusService.reportUrgent.pipe(takeUntil(this.destroy$)).subscribe(urgent => this.reportUrgent = urgent);
    this.statusService.refresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelected(event: any): void {
    this.result = null;
    this.fileError = '';

    const file: File = event?.target?.files?.[0];
    if (!file) { this.fileName = ''; this.base64Content = ''; return; }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.fileError = 'Please select a .csv file.';
      this.fileName = '';
      this.base64Content = '';
      if (event.target) { event.target.value = ''; }
      return;
    }

    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result as string;
      const comma = res.indexOf(',');
      this.base64Content = comma >= 0 ? res.substring(comma + 1) : res;
    };
    reader.onerror = () => { this.fileError = 'Could not read the selected file.'; };
    reader.readAsDataURL(file);
  }

  onUpload(): void {
    if (!this.selectedWeek) { this.fileError = 'Please select the week ending first.'; return; }
    if (!this.base64Content) { this.fileError = 'Please choose a CSV file first.'; return; }

    this.uploading = true;
    this.result = null;
    this.fileError = '';

    // Loader popup, matching the other screens.
    Swal.fire({
      title: 'Uploading report...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => { Swal.showLoading(); }
    });

    this.sfDataService.uploadHubReport({
      FileName: this.fileName,
      Base64Content: this.base64Content,
      WeekEnding: this.selectedWeek
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.uploading = false;
        this.result = this.normalize(res);
        Swal.close();
        if (this.result.success) {
          this.statusService.refresh(); // clears the menu/dashboard reminder
          Swal.fire({
            icon: 'success',
            title: 'Report received',
            html: `${this.result.rowsUpdated ?? 0} of ${this.result.rowsProcessed ?? 0} record(s) updated for week ending <strong>${this.result.weekEnding || this.formatWeek(this.selectedWeek)}</strong>.`
          });
          this.clearFile();
        } else {
          // Server returned 200 but not success (partial Salesforce failure).
          this.showErrorPopup(this.result);
        }
      },
      error: (err: any) => {
        this.uploading = false;
        Swal.close();
        const body = err?.error;

        if (body && (body.Errors || body.errors || body.Message || body.message)) {
          this.result = this.normalize(body);
          this.showErrorPopup(this.result);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Upload failed',
            text: 'Please try again, or contact support if the problem continues.'
          });
        }
      }
    });
  }

  reset(): void {
    this.clearFile();
    this.result = null;
    this.fileError = '';
  }

  private clearFile(): void {
    this.fileName = '';
    this.base64Content = '';
    const input = document.getElementById('csvFile') as HTMLInputElement | null;
    if (input) { input.value = ''; }
  }

  private showErrorPopup(result: UploadResult): void {
    const errs = result.errors || [];
    const top = errs.slice(0, 8)
      .map(e => `<li style="text-align:left;">${e.row ? 'Row ' + e.row + ': ' : ''}${e.message}</li>`)
      .join('');
    const more = errs.length > 8 ? `<p>…and ${errs.length - 8} more (see the list below).</p>` : '';
    Swal.fire({
      icon: 'error',
      title: 'File not accepted',
      html: `<p>${result.message || 'Please correct the issues and upload again.'}</p>` +
            (top ? `<ul style="margin:0 auto; display:inline-block;">${top}</ul>` : '') + more
    });
  }

  formatWeek(iso: string): string {
    if (!iso) { return ''; }
    const parts = iso.split('-');
    if (parts.length === 3) {
      return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}/${parts[0]}`;
    }
    return iso;
  }

  // The API may serialize as PascalCase or camelCase depending on config; accept both.
  private normalize(r: any): UploadResult {
    const rawErrors = r.Errors ?? r.errors ?? [];
    return {
      success: r.Success ?? r.success,
      status: r.Status ?? r.status,
      message: r.Message ?? r.message,
      rowsProcessed: r.RowsProcessed ?? r.rowsProcessed,
      rowsUpdated: r.RowsUpdated ?? r.rowsUpdated,
      rowsFailed: r.RowsFailed ?? r.rowsFailed,
      weekEnding: r.WeekEnding ?? r.weekEnding,
      errors: (rawErrors || []).map((e: any) => ({
        row: e.Row ?? e.row ?? null,
        column: e.Column ?? e.column ?? '',
        message: e.Message ?? e.message ?? ''
      }))
    };
  }
}
